import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Camera,
  ChevronDown,
  Globe,
  LogIn,
  LogOut,
  Menu,
  Search,
  Stamp,
  X,
} from "lucide-react";
import { useAuth } from "../../AuthContext";
import NotificationBell from "../NotificationBell";
import BottoneInstallaApp from "../BottoneInstallaApp";
import { useLang } from "../../LangContext";
import { langUrl, URL_LANGS } from "../../utils/langRoute";

const BORGHI_SEARCH = [
  { name: "Civitella del Tronto", slug: "civitella-del-tronto" },
  { name: "Castelli", slug: "castelli" },
  { name: "Assisi", slug: "assisi" },
  { name: "Montorio al Vomano", slug: "montorio-al-vomano" },
  { name: "Offida", slug: "offida" },
  { name: "Campli", slug: "campli" },
];

const NAV_LINKS = [
  { to: "/piazze", tKey: "nav.piazze" },
  { to: "/esplora-la-mappa", tKey: "nav.map3d" },
  { to: "/eventi", tKey: "nav.events" },
  { to: "/vetrina", tKey: "nav.merchants" },
];

const LANGS = ["it", "en", "fr", "de", "es"];

function readTimbriCount() {
  try {
    const raw = localStorage.getItem("6amico_passaporto_timbri");
    if (!raw) return 0;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.length;
    if (typeof parsed === "number") return parsed;
    return 0;
  } catch {
    return 0;
  }
}

const LINGUE_META = {
  it: { label: "Italiano", emoji: "🇮🇹" },
  en: { label: "English", emoji: "🇬🇧" },
  fr: { label: "Français", emoji: "🇫🇷" },
  de: { label: "Deutsch", emoji: "🇩🇪" },
  es: { label: "Español", emoji: "🇪🇸" },
};

/** Selettore lingua a scomparsa (IT/EN/FR/DE/ES) — condiviso navbar e footer.
 *  BLOCCO C: it/en/fr/es sono link <a href> veri alle URL per lingua
 *  (indicizzabili); le altre lingue restano bottoni → Google Translate,
 *  senza URL dedicata (non indicizzate). */
export function LanguageSwitch({ compact = false }) {
  const { lang, setLang } = useLang();
  const current = LANGS.includes(lang) ? lang : "it";
  const [aperto, setAperto] = useState(false);
  const containerRef = useRef(null);
  const { pathname, search } = useLocation();

  useEffect(() => {
    const handleClickFuori = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setAperto(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setAperto(false);
    };
    document.addEventListener("mousedown", handleClickFuori);
    document.addEventListener("touchstart", handleClickFuori);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handleClickFuori);
      document.removeEventListener("touchstart", handleClickFuori);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const pick = (code) => {
    try {
      localStorage.setItem("6amico_lang", code);
    } catch { /* noop */ }
    window.dispatchEvent(
      new CustomEvent("6amico_language_changed", { detail: { lang: code } })
    );
    setLang(code);
    setAperto(false);
  };

  return (
    <div ref={containerRef} className="relative inline-block text-left">
      <button
        type="button"
        onClick={() => setAperto((v) => !v)}
        aria-expanded={aperto}
        aria-haspopup="menu"
        aria-label={`Lingua attuale: ${LINGUE_META[current].label}. Clicca per cambiare`}
        data-testid="lang-switch"
        className={`flex items-center gap-1.5 px-3 rounded-full border border-stone-300 bg-white/90 hover:bg-stone-50 backdrop-blur text-stone-800 text-xs font-semibold tracking-wide shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-[#162d4a] ${
          compact ? "h-9" : "h-11"
        }`}
      >
        <Globe size={14} className="text-stone-500" aria-hidden />
        <span>{current.toUpperCase()}</span>
        <ChevronDown
          size={12}
          className={`text-stone-400 transition-transform duration-200 ${aperto ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>

      {aperto && (
        <div
          className="absolute right-0 mt-1.5 w-44 rounded-xl bg-white border border-stone-200 shadow-xl py-1.5 z-50"
          role="menu"
          aria-label="Seleziona lingua"
        >
          <div className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-stone-400 border-b border-stone-100 mb-1">
            Seleziona Lingua · Choose language
          </div>
          {LANGS.map((code) => {
            const meta = LINGUE_META[code];
            const isSelected = code === current;
            const optionClass = `w-full flex items-center justify-between px-3 py-2.5 text-xs text-left transition-colors ${
              isSelected
                ? "bg-[#162d4a] text-white font-semibold"
                : "text-stone-700 hover:bg-stone-100 font-medium"
            }`;
            const inner = (
              <>
                <span className="flex items-center gap-2">
                  <span className="text-sm leading-none" aria-hidden>{meta.emoji}</span>
                  <span>{meta.label}</span>
                </span>
                <span className={`text-[10px] tracking-wider uppercase ${isSelected ? "text-stone-200" : "text-stone-400"}`}>
                  {code}
                </span>
              </>
            );
            // it/en/fr/es: link vero alla URL per lingua (SEO).
            // Altre lingue (de, …): bottone → Google Translate, non indicizzate.
            if (code === "it" || URL_LANGS.includes(code)) {
              return (
                <a
                  key={code}
                  href={langUrl(code, pathname, search)}
                  role="menuitem"
                  aria-pressed={isSelected}
                  data-testid={`lang-option-${code}`}
                  className={optionClass}
                  onClick={() => {
                    try {
                      localStorage.setItem("6amico_lang", code);
                      localStorage.setItem("6amico_lang_explicit", "1");
                    } catch { /* noop */ }
                  }}
                >
                  {inner}
                </a>
              );
            }
            return (
              <button
                key={code}
                type="button"
                role="menuitem"
                onClick={() => pick(code)}
                aria-pressed={isSelected}
                data-testid={`lang-option-${code}`}
                className={optionClass}
              >
                {inner}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function NavbarTerritorialeTop() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [timbri, setTimbri] = useState(0);
  const searchRef = useRef(null);
  const moreRef = useRef(null);

  useEffect(() => {
    setTimbri(readTimbriCount());
    const bump = () => setTimbri(readTimbriCount());
    window.addEventListener("6amico_timbro_aggiunto", bump);
    window.addEventListener("storage", bump);
    return () => {
      window.removeEventListener("6amico_timbro_aggiunto", bump);
      window.removeEventListener("storage", bump);
    };
  }, []);

  useEffect(() => {
    setDrawer(false);
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onDoc = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setOpen(false);
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const results = query.trim()
    ? BORGHI_SEARCH.filter((b) =>
        b.name.toLowerCase().includes(query.trim().toLowerCase())
      )
    : BORGHI_SEARCH;

  const goBorgo = (slug) => {
    setOpen(false);
    setQuery("");
    navigate(`/comune/${slug}`);
  };

  return (
    <header
      className="sticky top-0 z-40 bg-[#fdfbf7]/95 backdrop-blur border-b border-[#eedfcb]"
      data-testid="navbar-territoriale"
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex items-center gap-3 h-16">
        <Link
          to="/"
          className="flex items-center gap-2.5 shrink-0 min-h-[48px]"
          aria-label="6AMICO — torna alla home"
        >
          <span
            className="w-10 h-10 rounded-full bg-[#c26d4a] text-white font-serif font-bold text-lg flex items-center justify-center border-2 border-[#a85837] shadow-sm"
            aria-hidden
          >
            6a
          </span>
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-serif font-bold text-xl text-stone-950 tracking-wide">
              6AMICO
            </span>
            <span className="text-[10px] font-sans font-medium text-stone-600 tracking-wide mt-0.5">
              {t("nav.tagline") || "La rete dei borghi d'Italia · Dal 2013"}
            </span>
          </span>
        </Link>

        <div ref={searchRef} className="nav-search relative flex-1 max-w-md min-w-[150px]">
          <label htmlFor="nav-search-borgo" className="sr-only">
            {t("nav.search_aria") || "Cerca un borgo"}
          </label>
          <div className="flex items-center gap-2 h-11 px-3 rounded-full bg-white border border-[#eedfcb] focus-within:border-[#c26d4a]">
            <Search size={16} className="text-stone-700 shrink-0" aria-hidden />
            <input
              id="nav-search-borgo"
              type="search"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && results[0]) goBorgo(results[0].slug);
                if (e.key === "Escape") setOpen(false);
              }}
              placeholder={t("nav.search_placeholder") || "Cerca un borgo…"}
              autoComplete="off"
              className="w-full bg-transparent text-sm text-stone-950 placeholder:text-stone-600 outline-none"
              aria-expanded={open}
              aria-controls="nav-search-list"
              role="combobox"
            />
          </div>
          {open && results.length > 0 && (
            <ul
              id="nav-search-list"
              role="listbox"
              className="absolute top-full left-0 right-0 mt-2 bg-white border border-[#eedfcb] rounded-xl shadow-lg overflow-hidden z-50"
            >
              {results.map((b) => (
                <li key={b.slug} role="option" aria-selected="false">
                  <button
                    type="button"
                    onClick={() => goBorgo(b.slug)}
                    className="w-full text-left px-4 min-h-[44px] flex items-center text-sm text-stone-800 hover:bg-[#f7efe6] hover:text-stone-950"
                  >
                    {b.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <nav className="hidden lg:flex items-center gap-1" aria-label="Navigazione principale">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="min-h-[44px] inline-flex items-center px-3 text-sm font-sans font-semibold text-stone-800 hover:text-[#a85837] transition-colors"
            >
              {t(l.tKey)}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2 ml-auto shrink-0">
          {(user || timbri > 0) && (
            <span
              className="min-h-[44px] inline-flex items-center gap-1.5 px-3.5 rounded-full bg-[#b8860b] text-white text-sm font-sans font-bold shadow-sm"
              title={`${t("nav.passport") || "Passaporto dei borghi"} — ${timbri}/6`}
              data-testid="passaporto-badge"
            >
              <Stamp size={15} aria-hidden />
              {timbri}/6
            </span>
          )}
          <div ref={moreRef} className="relative">
            <button
              type="button"
              onClick={() => setMoreOpen((v) => !v)}
              aria-expanded={moreOpen}
              aria-haspopup="menu"
              className="min-h-[44px] inline-flex items-center gap-1.5 px-3.5 rounded-full border border-[#ecd9c2] bg-[#f7efe6] text-sm font-sans font-semibold text-stone-900 hover:border-[#162d4a] transition-colors"
              data-testid="nav-more-toggle"
            >
              {t("nav.more") || "Altro"}
              <ChevronDown size={13} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`} aria-hidden />
            </button>
            {moreOpen && (
              <div
                role="menu"
                aria-label="Altre funzioni"
                className="absolute right-0 mt-1.5 w-56 rounded-xl bg-white border border-stone-200 shadow-xl py-1.5 z-50"
              >
                <Link
                  to="/qr"
                  role="menuitem"
                  onClick={() => setMoreOpen(false)}
                  className="w-full flex items-center gap-2.5 px-4 min-h-[44px] text-sm text-stone-800 hover:bg-[#f7efe6]"
                >
                  <Camera size={15} className="text-stone-500" aria-hidden />
                  {t("nav.ar_qr") || "Visore AR & QR"}
                </Link>
                <div className="px-3 py-1.5" role="none">
                  <BottoneInstallaApp variante="card" />
                </div>
              </div>
            )}
          </div>
          <LanguageSwitch compact />
          {user ? (
            <>
              <NotificationBell />
              <button
                type="button"
                onClick={logout}
                className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-full text-stone-800 hover:bg-[#f7efe6]"
                aria-label={t("nav.logout") || "Esci"}
                title={t("nav.logout") || "Esci"}
              >
                <LogOut size={18} aria-hidden />
              </button>
            </>
          ) : (
            <Link
              to="/accedi"
              className="min-h-[44px] inline-flex items-center gap-1.5 px-4 rounded-full bg-stone-900 text-white text-sm font-sans font-semibold hover:bg-stone-700 transition-colors"
            >
              <LogIn size={15} aria-hidden />
              {t("nav.login") || "Accedi"}
            </Link>
          )}
        </div>

        <button
          type="button"
          onClick={() => setDrawer((v) => !v)}
          className="md:hidden ml-auto min-h-[48px] min-w-[48px] inline-flex items-center justify-center rounded-full text-stone-900 hover:bg-[#f7efe6]"
          aria-expanded={drawer}
          aria-label={drawer ? "Chiudi menu" : "Apri menu"}
          data-testid="nav-mobile-toggle"
        >
          {drawer ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {drawer && (
        <nav
          className="md:hidden border-t border-[#eedfcb] bg-[#fdfbf7] px-4 py-3 flex flex-col gap-1"
          aria-label="Menu mobile"
          data-testid="nav-mobile-drawer"
        >
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="min-h-[48px] flex items-center px-3 rounded-lg text-base font-sans font-semibold text-stone-900 hover:bg-[#f7efe6]"
            >
              {t(l.tKey)}
            </Link>
          ))}
          <Link
            to="/qr"
            className="min-h-[48px] flex items-center gap-2 px-3 rounded-lg text-base font-sans font-semibold text-stone-900 hover:bg-[#f7efe6]"
          >
            <Camera size={17} aria-hidden />
            {t("nav.ar_qr") || "Visore AR & QR"}
          </Link>
          <div className="flex items-center gap-2 px-3 py-2">
            <span className="inline-flex items-center gap-1.5 px-3.5 min-h-[44px] rounded-full bg-[#b8860b] text-white text-sm font-sans font-bold shadow-sm">
              <Stamp size={15} aria-hidden />
              {timbri}/6
            </span>
            <LanguageSwitch />
          </div>
          <div className="px-3 pb-1">
            <BottoneInstallaApp variante="card" />
          </div>
          {user ? (
            <button
              type="button"
              onClick={logout}
              className="min-h-[48px] flex items-center gap-2 px-3 rounded-lg text-base font-sans font-semibold text-stone-900 hover:bg-[#f7efe6]"
            >
              <LogOut size={17} aria-hidden />
              {t("nav.logout") || "Esci"}
            </button>
          ) : (
            <Link
              to="/accedi"
              className="min-h-[48px] flex items-center justify-center gap-2 px-3 rounded-lg bg-stone-900 text-white text-base font-sans font-semibold"
            >
              <LogIn size={17} aria-hidden />
              {t("nav.login_or_register") || "Accedi o registrati"}
            </Link>
          )}
        </nav>
      )}
    </header>
  );
}
